import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import GeneratingPanel from '../components/GeneratingPanel';
import GlitchTitle from '../components/GlitchTitle';
import Countdown from '../components/Countdown';
import IsoCube from '../components/IsoCube';
import BrandMark from '../components/BrandMark';
import { apiFetch, clearParticipantToken, getParticipantToken } from '../lib/api';
import type { ApiError } from '../lib/api';
import {
  decodeParticipantToken,
  joinAsParticipant,
} from '../lib/participantSession';
import { setActiveSessionId } from '../lib/useActiveSession';
import { useGameState } from '../lib/useGameState';
import { useLayoutPreview } from '../lib/layoutPreviewContext';
import { pickUniqueVoteLeader } from '../lib/pickUniqueVoteLeader';
import type { Participant as ParticipantType } from '../lib/types';
import { PROMPT_CHAR_LIMIT } from '../lib/promptCharLimit';
import { isParticipantDeviceEnabled } from '../lib/participantDeviceCookie';
import styles from './Participant.module.css';

interface ActiveSession {
  sessionId: string;
  participantId: string;
  slot: number;
}

export default function Participant() {
  const [session, setSession] = useState<ActiveSession | null>(() => {
    const token = getParticipantToken();
    if (!token) return null;
    const dec = decodeParticipantToken(token);
    if (!dec) return null;
    return { sessionId: dec.session_id, participantId: dec.participant_id, slot: dec.slot };
  });

  if (!session) {
    return (
      <JoinForm
        onJoined={(s) => {
          setActiveSessionId(s.sessionId);
          setSession(s);
        }}
      />
    );
  }
  return <ActiveParticipant session={session} onLeave={() => {
    clearParticipantToken();
    setSession(null);
  }} />;
}

/* =====================================================================
 * Join form (login partecipante)
 * ===================================================================== */

interface JoinFormProps {
  onJoined: (s: ActiveSession) => void;
}

function JoinForm({ onJoined }: JoinFormProps) {
  const [searchParams] = useSearchParams();
  const gameNameFromUrl = useMemo(() => {
    const raw = searchParams.get('session');
    if (!raw?.trim()) return '';
    try {
      return decodeURIComponent(raw.trim());
    } catch {
      return raw.trim();
    }
  }, [searchParams]);

  const [deviceReady, setDeviceReady] = useState(() => isParticipantDeviceEnabled());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const submitLockRef = useRef(false);

  useEffect(() => {
    const sync = () => setDeviceReady(isParticipantDeviceEnabled());
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (submitLockRef.current) return;
    if (!deviceReady) {
      setError('Accesso negato. Device non abilitato.');
      return;
    }
    setError(null);
    setLoading(true);
    submitLockRef.current = true;
    try {
      const r = await joinAsParticipant({
        session: gameNameFromUrl,
        name: name.trim(),
        email: email.trim(),
      });
      onJoined({
        sessionId: r.participant.session_id,
        participantId: r.participant.id,
        slot: r.participant.slot,
      });
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  }

  if (!gameNameFromUrl) {
    return (
      <div className={styles.joinContainer}>
        <IsoCube uid="part-tr" className={`${styles.joinCube} ${styles.joinCubeTopRight}`} />
        <IsoCube uid="part-bl" className={`${styles.joinCube} ${styles.joinCubeBottomLeft}`} />
        <div className={styles.joinCard}>
          <div className={styles.joinCardTop}>
            <BrandMark tone="light" />
            <div className={styles.joinBadge}>
              <span className={styles.joinBadgeDot} />
              PARTECIPANTE
            </div>
          </div>
          <div className={styles.joinHeader}>
            <span className={styles.joinEyebrow}>« LINK NON VALIDO</span>
            <GlitchTitle text="MANCA·IL·NOME·GARA" className={styles.joinTitle} as="h1" />
          </div>
          <p className={styles.joinHint}>
            Apri il link inviato dalla regia: deve contenere il nome della gara, ad esempio{' '}
            <span className={styles.joinMono}>participant?session=Nome%20della%20gara</span>. Se il
            dispositivo non è abilitato, chiedi alla regia di configurarlo.
          </p>
        </div>
      </div>
    );
  }

  if (!deviceReady) {
    return (
      <div className={styles.joinContainer}>
        <IsoCube uid="part-tr" className={`${styles.joinCube} ${styles.joinCubeTopRight}`} />
        <IsoCube uid="part-bl" className={`${styles.joinCube} ${styles.joinCubeBottomLeft}`} />
        <div className={styles.joinCard}>
          <div className={styles.joinCardTop}>
            <BrandMark tone="light" />
            <div className={styles.joinBadge}>
              <span className={styles.joinBadgeDot} />
              PARTECIPANTE
            </div>
          </div>
          <p className={styles.joinAccessDenied} role="alert">
            Accesso negato. Device non abilitato.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.joinContainer}>
      <IsoCube uid="part-tr" className={`${styles.joinCube} ${styles.joinCubeTopRight}`} />
      <IsoCube uid="part-bl" className={`${styles.joinCube} ${styles.joinCubeBottomLeft}`} />

      <div className={styles.joinCard}>
        <div className={styles.joinCardTop}>
          <BrandMark tone="light" />
          <div className={styles.joinBadge}>
            <span className={styles.joinBadgeDot} />
            PARTECIPANTE
          </div>
        </div>

        <div className={styles.joinHeader}>
          <span className={styles.joinEyebrow}>« ACCESSO GARA</span>
          <GlitchTitle text="ENTRA·IN·GARA" className={styles.joinTitle} as="h1" />
        </div>

        <form className={styles.joinForm} onSubmit={submit}>
          <div className={styles.joinField}>
            <label htmlFor="name">NOME / NICKNAME</label>
            <input
              id="name"
              required
              maxLength={40}
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className={styles.joinField}>
            <label htmlFor="email">EMAIL</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              placeholder="nome@esempio.it"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error ? <div className={styles.joinError}>{error}</div> : null}
          <button type="submit" className={styles.joinSubmit} disabled={loading}>
            {loading ? 'JOIN…' : (
              <>ENTRA <span aria-hidden="true">→</span></>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

/* =====================================================================
 * Sessione attiva — orchestra le 4 viste in base allo stato della gara
 * ===================================================================== */

interface ActiveProps {
  session: ActiveSession;
  onLeave: () => void;
}

export function ActiveParticipant({ session, onLeave }: ActiveProps) {
  const layoutPreview = useLayoutPreview();
  const { state, participants, loading } = useGameState(session.sessionId);

  const me = useMemo(
    () => participants.find((p) => p.id === session.participantId) ?? null,
    [participants, session.participantId],
  );

  const [prompt, setPrompt] = useState<string>(me?.prompt ?? '');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [lastSyncMs, setLastSyncMs] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string>(me?.prompt ?? '');

  // Auto-logout robusta (round change, kick, wipe).
  const baselineRoundRef = useRef<number | null>(null);
  const everSeenSelfRef = useRef(false);
  const leaveKickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (state?.round == null) return;
    if (baselineRoundRef.current === null) {
      baselineRoundRef.current = state.round;
      return;
    }
    if (state.round !== baselineRoundRef.current) {
      onLeave();
    }
  }, [state?.round, onLeave]);

  useEffect(() => {
    if (me) {
      everSeenSelfRef.current = true;
      if (leaveKickTimerRef.current) {
        clearTimeout(leaveKickTimerRef.current);
        leaveKickTimerRef.current = null;
      }
      return;
    }
    if (!everSeenSelfRef.current) return;
    if (loading) return;

    if (leaveKickTimerRef.current) clearTimeout(leaveKickTimerRef.current);
    leaveKickTimerRef.current = window.setTimeout(() => {
      leaveKickTimerRef.current = null;
      onLeave();
    }, 500);

    return () => {
      if (leaveKickTimerRef.current) {
        clearTimeout(leaveKickTimerRef.current);
        leaveKickTimerRef.current = null;
      }
    };
  }, [me, onLeave, loading]);

  /** Applica il prompt dal server solo se non ci sono battute locali oltre l’ultimo POST riuscito (evita stale fetch / Realtime). */
  useEffect(() => {
    if (!me) return;
    if (prompt !== lastSentRef.current) return;
    if (me.prompt !== prompt) {
      setPrompt(me.prompt);
      lastSentRef.current = me.prompt;
    }
  }, [me?.prompt, prompt, me]);

  const winner = useMemo<ParticipantType | null>(() => {
    if (state?.status !== 'ENDED' || participants.length === 0) return null;
    return pickUniqueVoteLeader(participants);
  }, [state?.status, participants]);

  const schedulePromptSave = useCallback(
    (next: string) => {
      if (layoutPreview) {
        lastSentRef.current = next;
        setSavedAt(Date.now());
        setSavingError(null);
        setLastSyncMs(0);
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        if (next === lastSentRef.current) return;
        const t0 = performance.now();
        try {
          await apiFetch(
            '/api/participant/prompt',
            {
              method: 'POST',
              body: JSON.stringify({ prompt: next }),
            },
            { withParticipant: true },
          );
          lastSentRef.current = next;
          setSavedAt(Date.now());
          setSavingError(null);
          setLastSyncMs(Math.round(performance.now() - t0));
        } catch (e) {
          setSavingError((e as ApiError).message);
        }
      }, 150);
    },
    [layoutPreview],
  );

  const promptEditable = state?.status === 'WAITING_FOR_PLAYERS' || state?.status === 'WRITING';

  const onPromptChange = useCallback(
    (value: string) => {
      setPrompt(value);
      if (promptEditable) schedulePromptSave(value);
    },
    [promptEditable, schedulePromptSave],
  );

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  /** Griglia canvas globale: disattiva su scrittura prompt e vincitore (sfondi pieni). */
  useEffect(() => {
    const plain =
      state?.status === 'WRITING' ||
      state?.status === 'WAITING_FOR_PLAYERS' ||
      state?.status === 'ENDED';
    document.body.classList.toggle('surface-no-grid', !!plain);
    return () => {
      document.body.classList.remove('surface-no-grid');
    };
  }, [state?.status]);

  /* ----- VIEW dispatcher ----- */

  // 1) WRITING — surface colorata con prompt editor.
  if (state?.status === 'WRITING' || state?.status === 'WAITING_FOR_PLAYERS') {
    return (
      <WritingSurface
        me={me}
        state={state}
        prompt={prompt}
        onPromptChange={onPromptChange}
        savedAt={savedAt}
        savingError={savingError}
        lastSyncMs={lastSyncMs}
      />
    );
  }

  // 2) GENERATING — fullscreen nero con cubo + glitch + progress + countdown.
  if (state?.status === 'GENERATING') {
    return <GeneratingScene me={me} session={session} state={state} />;
  }

  // 3) VOTING — fullscreen colorato col badge nome + "VOTING" enorme.
  if (state?.status === 'VOTING') {
    return <VotingScene me={me} state={state} />;
  }

  // 4) ENDED — fullscreen colore vincitore + nome gigante + stats.
  if (state?.status === 'ENDED') {
    const totalVotes = participants.reduce((acc, p) => acc + p.votes, 0);
    return (
      <WinnerScene
        winner={winner}
        round={state.round}
        totalVotes={totalVotes}
        participants={participants}
      />
    );
  }

  // 5) IDLE / fallback — pannello sobrio in attesa di START_ROUND.
  return (
    <div className={styles.idleStage}>
          <BrandMark tone="light" className={styles.idleBrand} />
      <div className={styles.idleCenter}>
        <span className={styles.idleEyebrow}>SESSIONE AGGANCIATA · IN ATTESA REGIA</span>
        <GlitchTitle
          text={me?.name?.toUpperCase() ?? 'PARTECIPANTE'}
          className={styles.idleName}
        />
      </div>
      <button className={styles.idleLeave} onClick={onLeave}>
        ← LASCIA LA GARA
      </button>
    </div>
  );
}

/* =====================================================================
 * WritingSurface — sfondo colorato + top bar + tema + textarea nera +
 * footer stato sync (salvataggio automatico in digitazione)
 * ===================================================================== */

interface WritingProps {
  me: ParticipantType | null;
  state: NonNullable<ReturnType<typeof useGameState>['state']>;
  prompt: string;
  onPromptChange: (v: string) => void;
  savedAt: number | null;
  savingError: string | null;
  lastSyncMs: number | null;
}

function WritingSurface({
  me,
  state,
  prompt,
  onPromptChange,
  savedAt,
  savingError,
  lastSyncMs,
}: WritingProps) {
  const color = me?.color ?? 'var(--participant-one)';
  const writing = state.status === 'WRITING';

  return (
    <div className={styles.write} style={{ backgroundColor: color }}>
      {/* Top bar: nome a sx, timer pill a dx */}
      <header className={styles.writeTopBar}>
        <div className={styles.writeIdentity}>
          <div className={styles.writeIdentityText}>
            <span className={styles.writeIdentityLabel}>PARTECIPANTE</span>
            <span className={styles.writeIdentityName}>
              {(me?.name ?? '—').toUpperCase()}
            </span>
          </div>
        </div>

        <div className={styles.writeTimerPill}>
          <span className={styles.writeTimerDot} style={{ background: color }} />
          <span className={styles.writeTimerLabel}>
            {writing ? 'SCRITTURA' : 'IN ATTESA'}
          </span>
          {writing ? (
            <span className={styles.writeTimerValue}>
              <Countdown startIso={state.start_time} durationSec={state.duration_sec} />
            </span>
          ) : (
            <span className={styles.writeTimerValue}>—:—</span>
          )}
        </div>
      </header>

      <div className={styles.writeDivider} />

      {/* Tema */}
      <div className={styles.writeThemeWrap}>
        <span className={styles.writeThemeLabel}>
          TEMA · ROUND {String(state.round).padStart(2, '0')} ·
        </span>
        <h1 className={styles.writeTheme}>
          {(state.theme ?? '—').toUpperCase()}
        </h1>
      </div>

      {/* Editor */}
      <div className={styles.writeEditorBox}>
        <span className={styles.writeEditorLabel} style={{ color }}>
          PROMPT
        </span>
        <textarea
          className={styles.writeTextarea}
          style={{ color, caretColor: color }}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={writing ? 'scrivi qui il tuo prompt…' : 'in attesa che la regia avvii la scrittura…'}
          maxLength={PROMPT_CHAR_LIMIT}
          autoFocus
          disabled={!writing}
          aria-label="Prompt"
        />
      </div>

      {/* Footer */}
      <footer className={styles.writeFooter}>
        <div className={styles.writeFooterMeta}>
          <span>
            {savingError ? (
              <span className={styles.writeFooterError}>err {savingError}</span>
            ) : (
              <>sync {lastSyncMs != null ? `${lastSyncMs}ms` : '—'}</>
            )}
          </span>
          <span>·</span>
          <span>
            realtime{' '}
            {savedAt && !savingError ? <span aria-hidden="true">✓</span> : '…'}
          </span>
        </div>
      </footer>
    </div>
  );
}

/* =====================================================================
 * GeneratingScene — nero, brand top-left, cubo grande centrato, glitch
 * "GENERAZIONE…" + progress bar a 5 segmenti + countdown "VOTO TRA".
 * ===================================================================== */

interface GeneratingProps {
  me: ParticipantType | null;
  session: ActiveSession;
  state: NonNullable<ReturnType<typeof useGameState>['state']>;
}

function GeneratingScene({ me, session, state }: GeneratingProps) {
  const color = me?.color ?? 'var(--participant-one)';

  return (
    <div className={styles.gen}>
      <BrandMark tone="light" className={styles.genBrand} />

      <GeneratingPanel
        variant="fullscreen"
        color={color}
        name={me?.name}
        slot={session.slot}
        generationFinishedAt={state.generation_finished_at}
        cubeUid="gen-cube"
      />
    </div>
  );
}

/* =====================================================================
 * VotingScene — allineata a generazione / no-session: nero, BrandMark,
 * identità (punto colore slot), cubo, glitch "VOTING", barre, countdown.
 * ===================================================================== */

interface VotingProps {
  me: ParticipantType | null;
  state: NonNullable<ReturnType<typeof useGameState>['state']>;
}

function VotingScene({ me, state }: VotingProps) {
  const color = me?.color ?? 'var(--participant-one)';

  return (
    <div className={styles.vote}>
      <BrandMark tone="light" className={styles.voteBrand} />

      <div className={styles.voteCenter}>
        <span className={styles.voteIdentity} style={{ color }}>
          <span className={styles.voteIdentityDot} style={{ background: color }} aria-hidden />
          {(me?.name ?? 'PARTECIPANTE').toUpperCase()}
        </span>

        <div className={styles.voteCubeWrap}>
          <IsoCube uid="vote-cube" className={styles.voteCube} />
        </div>

        <GlitchTitle text="VOTING" className={styles.voteTitle} as="h1" />

        <div className={styles.voteProgress} aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={styles.voteProgressBar}
              style={{
                animationDelay: `${i * 220}ms`,
                background: color,
              }}
            />
          ))}
        </div>

        <div className={styles.voteCountdownBlock}>
          <span className={styles.voteCountdownLabel}>CHIUSURA</span>
          <span className={styles.voteCountdownValue}>
            <Countdown
              startIso={state.voting_start_time}
              durationSec={state.voting_duration_sec}
            />
          </span>
          <span className={styles.voteCountdownNote}>
            il pubblico sta votando · resta in scena
          </span>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
 * WinnerScene — sfondo colore VINCITORE, brand top-left, "★ VINCITORE
 * ROUND NN ★", nome gigante, stats voti+%, footer "complimenti…".
 * ===================================================================== */

interface WinnerProps {
  winner: ParticipantType | null;
  round: number;
  totalVotes: number;
  participants: ParticipantType[];
}

function WinnerScene({ winner, round, totalVotes, participants }: WinnerProps) {
  const sorted = useMemo(
    () => [...participants].sort((a, b) => a.slot - b.slot),
    [participants],
  );

  if (!winner) {
    return (
      <div className={styles.winnerZeroWrap}>
        <BrandMark tone="light" className={styles.winnerZeroBrand} />
        <div className={styles.winnerZeroCenter}>
          <span className={styles.winnerZeroEyebrow}>
            ROUND {String(round).padStart(2, '0')} · TERMINATO
          </span>
          <h1 className={styles.winnerZeroTitle}>NESSUN VINCITORE</h1>
          <p className={styles.winnerZeroSub}>Parità al vertice o tutti a zero voti: nessun vincitore eletto.</p>
          <ul className={styles.winnerZeroList}>
            {sorted.map((p) => (
              <li key={p.id} className={styles.winnerZeroRow}>
                <span className={styles.winnerZeroDot} style={{ background: p.color }} />
                <span className={styles.winnerZeroName}>{p.name.toUpperCase()}</span>
                <span className={styles.winnerZeroNums}>
                  <strong>0</strong> voti · <strong>0</strong>%
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const color = winner.color;
  const pct = totalVotes > 0 ? Math.round((winner.votes / totalVotes) * 100) : 0;

  return (
    <div className={styles.winner} style={{ backgroundColor: color }}>
      <BrandMark tone="dark" className={styles.winnerBrand} />

      <div className={styles.winnerCenter}>
        <span className={styles.winnerEyebrow}>
          ★ VINCITORE ROUND {String(round).padStart(2, '0')} ★
        </span>
        <h1 className={styles.winnerName}>{winner.name.toUpperCase()}</h1>
        <div className={styles.winnerStats}>
          <strong>{winner.votes}</strong> voti · <strong>{pct}%</strong>
        </div>
        <span className={styles.winnerSub}>
          complimenti · prossimo round in arrivo
        </span>
      </div>
    </div>
  );
}
